"use client"

import React from "react"
import { mergeAttributes, type Command } from "@tiptap/core"
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react"
import Image from "@tiptap/extension-image"

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        resizableImage: {
            setImageSize: (attrs: { width?: number | string; height?: number | string }) => ReturnType
            setImageWidth: (width: number | string) => ReturnType
            setImageHeight: (height: number | string) => ReturnType
        }
    }
}

// ===== NodeView em React =====
const ResizableImageComponent = (props: any) => {
    const { node, updateAttributes, selected } = props
    const { src, alt, title, width, height, textAlign } = node.attrs as {
        src: string
        alt?: string
        title?: string
        width?: number | string | null
        height?: number | string | null
        textAlign?: "left" | "center" | "right" | "justify" | null
    }

    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const boxRef = React.useRef<HTMLDivElement>(null)
    const imgRef = React.useRef<HTMLImageElement>(null)

    const [naturalRatio, setNaturalRatio] = React.useState<number | null>(null)
    const [dragging, setDragging] = React.useState(false)
    const [corner, setCorner] = React.useState<"tl" | "tr" | "bl" | "br">("br")
    const [startX, setStartX] = React.useState(0)
    const [startY, setStartY] = React.useState(0)
    const [startW, setStartW] = React.useState(0)

    React.useEffect(() => {
        const img = imgRef.current
        if (!img) return
        const setRatio = () => setNaturalRatio(img.naturalHeight / img.naturalWidth)
        if (img.complete && img.naturalWidth) setRatio()
        else img.addEventListener("load", setRatio)
        return () => img.removeEventListener("load", setRatio)
    }, [src])

    const onHandleDown =
        (c: "tl" | "tr" | "bl" | "br") => (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            setDragging(true)
            setCorner(c)
            setStartX(e.clientX)
            setStartY(e.clientY)
            const rectW =
                boxRef.current?.getBoundingClientRect().width ??
                imgRef.current?.getBoundingClientRect().width ??
                0
            setStartW(rectW)
            document.body.style.userSelect = "none"
        }

    React.useEffect(() => {
        if (!dragging) return
        const onMove = (e: MouseEvent) => {
            const dx = e.clientX - startX
            const dy = e.clientY - startY
            const hSign = corner === "tr" || corner === "br" ? +1 : -1
            const vSign = corner === "bl" || corner === "br" ? +1 : -1
            const deltaDiag = (dx * hSign + dy * vSign) / 2

            const parentW =
                wrapperRef.current?.getBoundingClientRect().width || 9999
            let newW = Math.round(startW + deltaDiag)
            newW = Math.max(48, Math.min(newW, Math.round(parentW)))

            if (naturalRatio && !e.altKey) {
                const newH = Math.round(newW * naturalRatio)
                updateAttributes({ width: newW, height: newH })
            } else {
                updateAttributes({ width: newW })
            }
        }
        const onUp = () => {
            setDragging(false)
            document.body.style.userSelect = ""
        }
        window.addEventListener("mousemove", onMove)
        window.addEventListener("mouseup", onUp)
        return () => {
            window.removeEventListener("mousemove", onMove)
            window.removeEventListener("mouseup", onUp)
        }
    }, [dragging, startX, startY, startW, corner, naturalRatio, updateAttributes])

    const imgStyle: React.CSSProperties = {
        width: typeof width === "number" ? `${width}px` : (width || undefined),
        height: typeof height === "number" ? `${height}px` : (height || "auto"),
        maxWidth: "100%",
        display: "block",
    }

    const justify =
        textAlign === "right"
            ? "flex-end"
            : textAlign === "center" || textAlign === "justify"
                ? "center"
                : "flex-start"

    return (
        <NodeViewWrapper
            className={`tt-resizable-image ${selected ? "is-selected" : ""}`}
            ref={wrapperRef}
            data-dragging={dragging ? "true" : "false"}
            data-align={textAlign ?? undefined}
            style={{
                display: "flex",
                justifyContent: justify,
                alignItems: "center",
                width: "100%",
            }}
            contentEditable={false}
        >
            <div
                ref={boxRef}
                className="tt-resizable-image-box"
                style={{
                    position: "relative",
                    display: "inline-block",
                    lineHeight: 0,
                }}
            >
                <img
                    ref={imgRef}
                    src={src}
                    alt={alt ?? ""}
                    title={title ?? ""}
                    style={imgStyle}
                />
                {(selected || dragging) && (
                    <>
                        <span className="tt-resize-handle tl" onMouseDown={onHandleDown("tl")} />
                        <span className="tt-resize-handle tr" onMouseDown={onHandleDown("tr")} />
                        <span className="tt-resize-handle bl" onMouseDown={onHandleDown("bl")} />
                        <span className="tt-resize-handle br" onMouseDown={onHandleDown("br")} />
                    </>
                )}
            </div>
        </NodeViewWrapper>
    )
}


// ===== Extensão =====
export const ResizableImage = Image.extend({
    name: "resizableImage",

    addAttributes(this: any) {
        return {
            ...(this.parent?.() ?? {}),
            width: { default: null },
            height: { default: null },
            textAlign: {
                default: null as "left" | "center" | "right" | "justify" | null,
                parseHTML: (el: HTMLElement) =>
                    (el.getAttribute?.("data-align") as any) ?? null,
                renderHTML: (attrs: { textAlign?: "left" | "center" | "right" | "justify" | null }) => ({
                    "data-align": attrs.textAlign ?? null,
                }),
            },
        }
    },

    renderHTML({ HTMLAttributes }: any) {
        const align = (HTMLAttributes["data-align"] ?? null) as
            | "left" | "center" | "right" | "justify" | null

        const alignStyle =
            align === "left"
                ? "display:block;margin:0;margin-right:auto;float:none;"
                : align === "center" || align === "justify"
                    ? "display:block;margin-left:auto;margin-right:auto;float:none;"
                    : align === "right"
                        ? "display:block;margin-left:auto;margin-right:0;float:none;"
                        : ""

        const widthStyle = HTMLAttributes.width
            ? `width:${typeof HTMLAttributes.width === "number" ? HTMLAttributes.width + "px" : HTMLAttributes.width};`
            : ""
        const heightStyle = HTMLAttributes.height
            ? `height:${typeof HTMLAttributes.height === "number" ? HTMLAttributes.height + "px" : HTMLAttributes.height};`
            : ""

        const attrs = mergeAttributes(HTMLAttributes, {
            style: [
                HTMLAttributes.style,
                "max-width:100%;",
                widthStyle,
                heightStyle,
                alignStyle,
            ].join(" "),
        })

        return ["img", attrs]
    },

    addNodeView() {
        return ReactNodeViewRenderer(ResizableImageComponent)
    },

    addCommands() {
        return {
            setImageSize:
                (attrs: { width?: number | string; height?: number | string }): Command =>
                    ({ commands }) => commands.updateAttributes(this.name, attrs),
            setImageWidth:
                (width: number | string): Command =>
                    ({ commands }) => commands.updateAttributes(this.name, { width }),
            setImageHeight:
                (height: number | string): Command =>
                    ({ commands }) => commands.updateAttributes(this.name, { height }),
        }
    },
})
